import { Search, X } from 'lucide-react';
import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type CSSProperties,
  type FC,
} from 'react';
import { normalizeForSearch, parseSearchTerms } from '@/utils/multiTermSearch';

/**
 * SearchInput - Componente padrão de busca do sistema Joshua
 *
 * Características:
 * - Busca em qualquer parte da palavra (não apenas início)
 * - Ignora acentuação (ex: "João" = "Joao")
 * - Case-insensitive
 * - Placeholder dinâmico baseado nos labels dos campos
 * - Placeholder responsivo: mostra apenas labels que cabem inteiros
 *
 * @example
 * <SearchInput
 *   value={searchTerm}
 *   onChange={setSearchTerm}
 *   fields={[
 *     { field: 'parte.nome', label: 'Cliente' },
 *     { field: 'descricao', label: 'Descrição' },
 *     { field: 'lancamento_categoria.categoria_contabil.nome', label: 'Categoria' }
 *   ]}
 * />
 * // Gera placeholder: "Buscar em Cliente, Descrição, Categoria..."
 * // Se não couber tudo, mostra apenas os que cabem: "Buscar em Cliente, Descrição..."
 */

// -----------------------------------------------------------------------------
// File-local companion types — HUB: all fields optional (consumers pass subsets).
// -----------------------------------------------------------------------------

interface SearchField {
  field: string;
  label: string;
}

interface PeriodToggleShape {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  show?: boolean;
}

interface SearchInputProps {
  value?: string;
  onChange?: (value: string) => void;
  fields?: SearchField[];
  placeholder?: string;
  className?: string;
  maxWidth?: number | string;
  periodToggle?: PeriodToggleShape;
}

// Minimal shape we accept for the `fieldConfig` entries on `filterData` —
// consumers pass either a raw string path or an object with `.field`.
interface FilterFieldConfig {
  field: string;
}

const SearchInput: FC<SearchInputProps> = ({
  value = '',
  onChange,
  fields = [],
  placeholder,
  className = '',
  maxWidth,
  periodToggle, // { checked, onChange, show }
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [computedPlaceholder, setComputedPlaceholder] = useState<string>('Buscar...');

  // Medir largura de texto usando canvas
  const measureText = useCallback((text: string, font: string): number => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) return 0;
    context.font = font;
    return context.measureText(text).width;
  }, []);

  // Calcular placeholder que cabe no input
  const calculatePlaceholder = useCallback(() => {
    if (placeholder) {
      setComputedPlaceholder(placeholder);
      return;
    }

    if (!fields || fields.length === 0) {
      setComputedPlaceholder('Buscar...');
      return;
    }

    const labels = fields.map((f) => f.label).filter(Boolean);
    if (labels.length === 0) {
      setComputedPlaceholder('Buscar...');
      return;
    }

    const input = inputRef.current;
    if (!input) {
      // Fallback: mostrar até 3 labels
      const displayLabels = labels.slice(0, 3);
      const suffix = labels.length > 3 ? '...' : '';
      setComputedPlaceholder(`Buscar por ${displayLabels.join(', ')}${suffix}`);
      return;
    }

    // Obter estilo do input para medir texto com a mesma fonte
    const style = window.getComputedStyle(input);
    const font = `${style.fontSize} ${style.fontFamily}`;

    // Largura disponível (descontando padding e ícone)
    const paddingLeft = parseFloat(style.paddingLeft) || 0;
    const paddingRight = parseFloat(style.paddingRight) || 0;
    const availableWidth = input.offsetWidth - paddingLeft - paddingRight - 10; // 10px margem de segurança

    const prefix = 'Buscar por ';
    const separator = ', ';
    const ellipsis = '...';

    const prefixWidth = measureText(prefix, font);
    const separatorWidth = measureText(separator, font);
    const ellipsisWidth = measureText(ellipsis, font);

    let currentWidth = prefixWidth;
    const fittingLabels: string[] = [];

    for (let i = 0; i < labels.length; i++) {
      const label = labels[i];
      const labelWidth = measureText(label, font);

      // Calcular largura necessária para adicionar este label
      const needsSeparator = fittingLabels.length > 0;
      const addedWidth = (needsSeparator ? separatorWidth : 0) + labelWidth;

      // Verificar se há mais labels depois deste
      const hasMoreLabels = i < labels.length - 1;

      // Largura total se adicionarmos este label
      const totalWidthWithLabel = currentWidth + addedWidth + (hasMoreLabels ? ellipsisWidth : 0);

      if (totalWidthWithLabel <= availableWidth) {
        fittingLabels.push(label);
        currentWidth += addedWidth;
      } else {
        // Este label não cabe inteiro, parar aqui
        break;
      }
    }

    // Montar placeholder final
    if (fittingLabels.length === 0) {
      setComputedPlaceholder('Buscar...');
    } else {
      const hasMoreLabels = fittingLabels.length < labels.length;
      const suffix = hasMoreLabels ? '...' : '';
      setComputedPlaceholder(`${prefix}${fittingLabels.join(separator)}${suffix}`);
    }
  }, [fields, placeholder, measureText]);

  // Recalcular quando componente monta ou fields mudam
  useEffect(() => {
    calculatePlaceholder();
  }, [calculatePlaceholder]);

  // Observar mudanças de tamanho do input
  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;

    const resizeObserver = new ResizeObserver(() => {
      calculatePlaceholder();
    });

    resizeObserver.observe(input);
    return () => resizeObserver.disconnect();
  }, [calculatePlaceholder]);

  const hasValue = value && value.length > 0;

  const handleClear = () => {
    onChange?.('');
    inputRef.current?.focus();
  };

  // CSS custom property on span — keep as-is but type with CSS custom prop escape.
  const periodLabelStyle: CSSProperties & Record<'--text-color', string> = {
    color: '#666',
    fontSize: '12px',
    fontWeight: 'normal',
    lineHeight: '1',
    '--text-color': '#666',
  };

  return (
    <div
      className={`ac-search ${hasValue ? 'ac-search--has-value' : ''} ${className}`}
      style={maxWidth ? { maxWidth } : undefined}
    >
      <Search size={16} className="ac-search__icon" />
      <input
        ref={inputRef}
        type="text"
        className="ac-search__input"
        placeholder={computedPlaceholder}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        style={periodToggle?.show ? { paddingRight: hasValue ? '140px' : '110px' } : undefined}
      />
      {periodToggle?.show && (
        <div
          style={{
            position: 'absolute',
            right: hasValue ? '40px' : '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 3,
            background: 'white',
            paddingLeft: '8px',
            paddingRight: '4px',
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              cursor: 'pointer',
              userSelect: 'none',
              whiteSpace: 'nowrap',
              margin: 0,
              padding: 0,
              border: 'none',
              background: 'transparent',
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <input
              type="checkbox"
              checked={periodToggle.checked}
              onChange={(e) => periodToggle.onChange?.(e.target.checked)}
              style={{
                cursor: 'pointer',
                accentColor: 'var(--color-primary)',
                margin: 0,
                width: '14px',
                height: '14px',
              }}
            />
            <span className="period-toggle-label" style={periodLabelStyle}>
              Todo período
            </span>
          </label>
        </div>
      )}
      {hasValue && (
        <button
          type="button"
          className="ac-search__clear"
          onClick={handleClear}
          title="Limpar busca"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
};

export default SearchInput;

/**
 * Função utilitária para normalizar texto (remover acentos e lowercase)
 * Delega para normalizeForSearch de multiTermSearch.ts (fonte canônica)
 */
export function normalizeText(text: string): string {
  if (!text) return '';
  return normalizeForSearch(String(text));
}

/**
 * Função utilitária para obter valor de um campo aninhado
 * Ex: getNestedValue(obj, 'parte.nome') retorna obj.parte.nome
 */
export function getNestedValue(obj: unknown, path: string): unknown {
  if (!obj || !path) return undefined;

  const keys = path.split('.');
  let value: unknown = obj;

  for (const key of keys) {
    if (value === null || value === undefined) return undefined;

    // Se for array, buscar em todos os itens
    if (Array.isArray(value)) {
      const results: unknown[] = value.map((item: unknown) =>
        getNestedValue(item, keys.slice(keys.indexOf(key)).join('.')),
      );
      return results.filter(Boolean).join(' ');
    }

    // Narrow to record-like before key access; keep runtime behaviour.
    if (typeof value === 'object') {
      value = (value as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }

  return value;
}

/**
 * Função utilitária para filtrar array de dados baseado no termo de busca
 * Busca em todos os campos especificados, ignorando acentos
 *
 * Suporta busca multi-termo (AND logic):
 * - "keystone cat6" busca itens que contenham "keystone" E "cat6"
 * - Cada termo pode estar em campos diferentes do mesmo item
 *
 * @example
 * const filtered = filterData(lancamentos, searchTerm, [
 *   { field: 'parte.nome' },
 *   { field: 'descricao' },
 *   { field: 'lancamento_categoria.categoria_contabil.nome' }
 * ]);
 */
export function filterData<T>(
  data: T[] | null | undefined,
  searchTerm: string | null | undefined,
  fields: Array<string | FilterFieldConfig>,
): T[] {
  if (!searchTerm || !data || data.length === 0) return (data as T[] | undefined) ?? [];

  const terms = parseSearchTerms(searchTerm);
  if (terms.length === 0) return data;

  return data.filter((item: T) => {
    // Concatenar todos os valores dos campos em uma string normalizada para busca
    const allValues = fields
      .map((fieldConfig) => {
        const fieldPath = typeof fieldConfig === 'string' ? fieldConfig : fieldConfig.field;
        const nested = getNestedValue(item, fieldPath);
        return nested !== null && nested !== undefined ? normalizeForSearch(String(nested)) : '';
      })
      .join(' ');

    // Verificar se TODOS os termos estão presentes (AND logic)
    return terms.every((term) => allValues.includes(term));
  });
}
